/**
 * A tiny insertion-ordered LRU cache with both an entry-count and an approximate
 * byte budget. Used to memoise expensive, idempotent render output (e.g. parsed
 * markdown) so finished chat messages are never re-parsed when they scroll back
 * into view. Mirrors the highlight cache used by the T3 Code reference UI.
 */
export class LruCache<V> {
	private readonly entries = new Map<string, { value: V; size: number }>();
	private totalBytes = 0;

	constructor(
		private readonly maxEntries: number,
		private readonly maxBytes: number
	) {}

	get(key: string): V | undefined {
		const entry = this.entries.get(key);
		if (entry === undefined) {
			return undefined;
		}
		// Touch: re-insert so it becomes the most-recently-used key.
		this.entries.delete(key);
		this.entries.set(key, entry);
		return entry.value;
	}

	set(key: string, value: V, size: number): void {
		const existing = this.entries.get(key);
		if (existing !== undefined) {
			this.totalBytes -= existing.size;
			this.entries.delete(key);
		}
		this.entries.set(key, { value, size });
		this.totalBytes += size;
		this.evict();
	}

	private evict(): void {
		while (
			this.entries.size > 0 &&
			(this.entries.size > this.maxEntries || this.totalBytes > this.maxBytes)
		) {
			const oldestKey = this.entries.keys().next().value as string;
			const oldest = this.entries.get(oldestKey);
			if (oldest !== undefined) {
				this.totalBytes -= oldest.size;
			}
			this.entries.delete(oldestKey);
		}
	}
}
